"use client";

import { c as _c } from "react/compiler-runtime";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { buildIframeComponentResolverScript, resolveComponentNames } from "./customCodeComponentResolve";
import { buildHtmlCustomCodeIframeSrcDoc } from "./customCodeHtmlIframeSrcDoc";
export type CustomCodeSourcePreviewProps = {
  codeType: "html" | "react" | "nextjs";
  sourceCode?: string;
  cssCode?: string;
  jsCode?: string;
  componentName?: string;
  minHeight?: number | string;
  style?: React.CSSProperties;
};
function buildPreviewHtml(params: {
  codeType: "html" | "react" | "nextjs";
  sourceCode: string;
  cssCode: string;
  jsCode: string;
  componentName?: string;
}): string {
  const {
    codeType,
    sourceCode,
    cssCode,
    jsCode,
    componentName
  } = params;
  const heightScript = `
(function(){
  function sendHeight(){
    var h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight, 120);
    try { window.parent.postMessage({ type: 'custom-code-height', height: h }, '*'); } catch(e){}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sendHeight);
  else sendHeight();
  setTimeout(sendHeight, 100);
  setTimeout(sendHeight, 500);
  setTimeout(sendHeight, 1500);
})();
`;
  if (codeType === "html") {
    return buildHtmlCustomCodeIframeSrcDoc({
      html: sourceCode,
      css: cssCode,
      js: jsCode,
      extraBodyScripts: [heightScript]
    });
  }

  // React or Next.js: run in iframe with React + Babel
  let processedCode = sourceCode || "";
  processedCode = processedCode.replace(/['"]use client['"];?\s*/g, "");
  processedCode = processedCode.replace(/import\s+\{[^}]+\}\s+from\s+['"]react['"];?\s*/g, "");
  processedCode = processedCode.replace(/import\s+React\s*,?\s*\{[^}]*\}?\s*from\s+['"]react['"];?\s*/g, "");
  processedCode = processedCode.replace(/import\s+React\s+from\s+['"]react['"];?\s*/g, "");
  processedCode = processedCode.replace(/export\s+default\s+function\s+(\w+)/g, "function $1");
  processedCode = processedCode.replace(/export\s+default\s+function\s*\(/g, "function DefaultComponent(");
  const componentResolver = buildIframeComponentResolverScript(resolveComponentNames(sourceCode, componentName));
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <script src="https://unpkg.com/react@18/umd/react.development.js"></script>
  <script src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
  <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  <style>body { margin: 0; padding: 0; font-family: system-ui, sans-serif; }</style>
</head>
<body>
  <div id="root"></div>
  <script type="text/babel" data-type="module">
    const { useState, useEffect, useCallback, useMemo, useRef, useReducer, useContext, useLayoutEffect, useId } = React;
    ${processedCode}
    ${componentResolver}
    if (ComponentName) {
      const root = ReactDOM.createRoot(document.getElementById('root'));
      root.render(React.createElement(ComponentName));
    } else {
      document.getElementById('root').innerHTML = '<p style="color: #ef4444; padding: 20px;">Component not found. Use export default (e.g. export default function MyHeader()) or set the component name in settings.</p>';
    }
    (function(){
      function sendHeight(){
        var h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight, 120);
        try { window.parent.postMessage({ type: 'custom-code-height', height: h }, '*'); } catch(e){}
      }
      setTimeout(sendHeight, 100);
      setTimeout(sendHeight, 500);
      setTimeout(sendHeight, 1500);
    })();
  </script>
</body>
</html>`;
}

/**
 * Renders a sandboxed iframe preview of custom code from source (no compiled code).
 * Used in the page builder when __compiledCode is missing so the custom component still shows a preview.
 */
const DEFAULT_MIN_HEIGHT = 200;
export const CustomCodeSourcePreview: React.FC<CustomCodeSourcePreviewProps> = t0 => {
  const $ = _c(26);
  const {
    codeType,
    sourceCode: t1,
    cssCode: t2,
    jsCode: t3,
    componentName,
    minHeight: t4,
    style: t5
  } = t0;
  const sourceCode = t1 === undefined ? "" : t1;
  const cssCode = t2 === undefined ? "" : t2;
  const jsCode = t3 === undefined ? "" : t3;
  const minHeight = t4 === undefined ? DEFAULT_MIN_HEIGHT : t4;
  let t6;
  if ($[0] !== t5) {
    t6 = t5 === undefined ? {} : t5;
    $[0] = t5;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const style = t6;
  const iframeRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(null);
  let t7;
  let t8;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const handler = e => {
        if (e.data?.type !== "custom-code-height" || typeof e.data.height !== "number") {
          return;
        }
        if (iframeRef.current?.contentWindow !== e.source) {
          return;
        }
        setContentHeight(e.data.height);
      };
      window.addEventListener("message", handler);
      return () => window.removeEventListener("message", handler);
    };
    t8 = [];
    $[2] = t7;
    $[3] = t8;
  } else {
    t7 = $[2];
    t8 = $[3];
  }
  useEffect(t7, t8);
  let t9;
  if ($[4] !== codeType || $[5] !== componentName || $[6] !== cssCode || $[7] !== jsCode || $[8] !== sourceCode) {
    t9 = buildPreviewHtml({
      codeType,
      sourceCode,
      cssCode,
      jsCode,
      componentName
    });
    $[4] = codeType;
    $[5] = componentName;
    $[6] = cssCode;
    $[7] = jsCode;
    $[8] = sourceCode;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const srcdoc = t9;
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setContentHeight(null);
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== srcdoc) {
    t11 = [srcdoc];
    $[11] = srcdoc;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  useEffect(t10, t11);
  const hasContent = codeType === "html" ? !!(sourceCode || cssCode || jsCode) : !!sourceCode;
  if (!hasContent) {
    const t12 = typeof minHeight === "number" ? `${minHeight}px` : minHeight;
    let t13;
    if ($[13] !== style || $[14] !== t12) {
      t13 = <div style={{
        minHeight: t12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--theme-elevation-50, #f3f4f6)",
        borderRadius: 8,
        border: "1px dashed var(--theme-elevation-200, #e5e7eb)",
        color: "var(--theme-elevation-500, #6b7280)",
        fontSize: 13,
        ...style
      }}>Custom code — add source to preview</div>;
      $[13] = style;
      $[14] = t12;
      $[15] = t13;
    } else {
      t13 = $[15];
    }
    return t13;
  }
  const minHeightPx = typeof minHeight === "number" ? minHeight : 200;
  const height = contentHeight == null ? undefined : Math.max(contentHeight, minHeightPx);
  const t12 = typeof minHeight === "number" ? `${minHeight}px` : minHeight;
  let t13;
  if ($[16] !== height) {
    t13 = height == null ? {} : {
      height: `${height}px`,
      maxHeight: "80vh"
    };
    $[16] = height;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== style || $[19] !== t12 || $[20] !== t13) {
    t14 = {
      width: "100%",
      minHeight: t12,
      ...t13,
      border: "none",
      borderRadius: 8,
      background: "#fff",
      ...style
    };
    $[18] = style;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = componentName ? `Preview: ${componentName}` : "Custom code preview";
  let t16;
  if ($[22] !== srcdoc || $[23] !== t14 || $[24] !== t15) {
    t16 = <iframe ref={iframeRef} sandbox="allow-scripts" srcDoc={srcdoc} style={t14} title={t15} />;
    $[22] = srcdoc;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  return t16;
};
export default CustomCodeSourcePreview;